<template>
  <div class="upload-section upload-section--default">
    <div class="upload-section__heading-wrapper">
      <h5>{{ headingText }}</h5>
      <div class="hide-mobile">
        <div :class="errorClass">
          <span
            class="icon icon--upload-error"
            aria-label="Upload Error"
          ></span>
          {{ errorMessage }}
        </div>
      </div>
    </div>
    <teleport to="#pinnedUpload">
      <div
        class="upload-remove"
        v-if="uploadedArtwork && uploadedArtwork.artwork"
        @click="handleRemoveArtwork"
      >
        Remove Photo
      </div>
      <ArtworkUploadButton
        v-else
        ref="ArtworkUploadButton"
        :title="buttonText"
        class="upload-button"
        :joineryHost="joineryHost"
        @upload-complete="handleUploadComplete($event)"
        @upload-error="handleUploadError($event)"
        @upload-initiated="handleUploadInitiated($event)"
      >
        Upload
      </ArtworkUploadButton>
    </teleport>
    <div ref="artworkContainer">
      <UploadedImage
        v-if="uploadedArtwork && uploadedArtwork.artwork"
        :uploadedArtwork="uploadedArtwork"
        @removeArtwork="handleRemoveArtwork($event)"
      />
      <ArtworkUploadButton
        v-else
        ref="ArtworkUploadButton"
        :title="buttonText"
        class="upload-button"
        :class="{'is-processing': isProcessing}"
        :joineryHost="joineryHost"
        @upload-complete="handleUploadComplete($event)"
        @upload-error="handleUploadError($event)"
        @upload-initiated="handleUploadInitiated($event)"
        @upload-progress="handleUploadProgress($event)"
      >
        Upload
      </ArtworkUploadButton>
      <div class="hide-desktop">
        <div :class="errorClass">
          <span
            class="icon icon--upload-error"
            aria-label="Upload Error"
          ></span>
          {{ errorMessage }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ArtworkUploadButton from '@framebridge/toolbox/ArtworkUploadButton';
import UploadedImage from './UploadedImage';

export default {
  name: 'UploadComponent',
  components: {
    ArtworkUploadButton,
    UploadedImage,
  },

  data() {
    return {
      joineryHost: SDG?.Data?.joineryHost || 'https://staging.framebridge.com',
      headingText: '',
      errorMessage: '',
      errorClass: 'hide',
      isPinned: false,
      buttonText: 'Upload Photo',
      uploadedArtwork: {},
      isProcessing: false,
      uploadProgress: 0,
      defaultText: '',
      uploadedText: '',
    };
  },
  mounted() {
    this.uploadedArtwork = this.getLocalStorageItem('uploadedArtwork');

    // set the heading text
    this.setHeadingText();
  },
  methods: {
    initializeObserver() {
      const elementToObserve = this.$refs.artworkContainer;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.isPinned = false;
            } else {
              // Element is not in view and toolbar is pinned
              this.isPinned = true;
            }
          });
        },
        {
          root: null,
          threshold: 0.9,
          rootMargin: '-90px',
        },
      );

      // Start observing the element
      observer.observe(elementToObserve);
    },
    getLocalStorageItem(key) {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    },

    handleUploadComplete(data) {
      localStorage.setItem('uploadedArtwork', JSON.stringify(data));
      this.uploadedArtwork = data;
      this.headingText = this.uploadedText;

      const changeEvent = new CustomEvent('uploadItem', {
        detail: {
          added: true,
          removed: false,
        },
      });

      window.dispatchEvent(changeEvent);
      this.isProcessing = false;
      this.$forceUpdate();
    },
    handleUploadError(data) {
      this.errorMessage = `${data || 'Unknown error'}`;
      this.errorClass = 'upload-error';
      this.isProcessing = false;
    },
    handleUploadInitiated() {
      this.errorClass = 'hide';
      this.buttonText = 'Uploading...';
      this.isProcessing = true;
    },
    handleRemoveArtwork() {
      localStorage.removeItem('uploadedArtwork');
      this.uploadedArtwork = null; // Reset the uploadedArtwork to null
      this.headingText = this.defaultText;

      const changeEvent = new CustomEvent('uploadItem', {
        detail: {
          added: false,
          removed: true,
        },
      });

      window.dispatchEvent(changeEvent);
      this.buttonText = 'Upload Photo';

      this.$forceUpdate();
    },
    handleUploadProgress(data) {
      const { percentComplete } = data;
      const roundedPercentComplete = Math.round(percentComplete);
      this.uploadProgress = { percentComplete: roundedPercentComplete };
      this.headingText = `${roundedPercentComplete}% Uploaded`;
    },
    setHeadingText() {
      // Set default and uploaded text from attributes in mounted lifecycle
      this.defaultText = this.$el.parentElement.getAttribute('data-section-heading')
      || 'Framing a digital photo? Upload here to see it in all frames.';
      this.uploadedText = this.$el.parentElement.getAttribute('data-section-uploaded-heading')
      || 'Previewing your digital photo in all frames. Select a frame to get started.';

      // set the heading text
      if (this.uploadedArtwork && this.uploadedArtwork.artwork) {
        this.headingText = this.uploadedText;
      } else {
        this.headingText = this.defaultText;
      }
    },
  },
};
</script>
